import { Box, Paper, Theme, Toolbar, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { GridColDef, GridRowModel, GridSortModel, DataGridProProps } from '@mui/x-data-grid-pro';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { DataList } from '../../components/DataList';
import { Maybe, Person } from '../../gql-types.generated';
import { fetchApteanAdminUsers } from './ApteanAdminUsersActions';
import { selectApteanAdminUsers } from './ApteanAdminUsersSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      flex: 1,
      display: 'flex',
      flexFlow: 'column nowrap',
      height: '100%',
      minHeight: 350,
    },
    usersTable: {
      flexGrow: 1,
      display: 'flex',
    },
    toolbar: {
      borderBottomWidth: 1,
      borderBottomColor: alpha(theme.palette.common.black, 0.15),
      borderBottomStyle: 'solid',
      padding: theme.spacing(1.5, 2.5),
    },
    title: {
      flex: '1 1 100%',
    },
  }),
);

const ApteanAdminUsers: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const rows = useSelector(selectApteanAdminUsers);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);

  useEffect(() => {
    dispatch(fetchApteanAdminUsers());
  }, []);

  const getUserRows = () =>
    rows?.map((row: Maybe<Person>) => {
      if (!row) return {} as GridRowModel;
      return {
        id: row.id,
        name: `${row.firstName} ${row.lastName}`,
        email: row.email,
      } as GridRowModel;
    }) as GridRowModel[];

  const columns: GridColDef[] = [
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'name',
      sortable: true,
      headerName: 'Name',
      flex: 0.4,
      minWidth: 130,
    },
    {
      resizable: false,
      disableReorder: true,
      disableColumnMenu: true,
      field: 'email',
      sortable: true,
      headerName: 'Email',
      flex: 0.6,
      minWidth: 130,
    },
  ];

  const gridOptions: DataGridProProps = {
    rows: getUserRows(),
    'aria-label': 'Aptean admin users table',
    columns,
    sortingOrder: ['desc', 'asc'],
    sortModel,
    onSortModelChange: setSortModel,
  };

  return (
    <Paper className={classes.paper} role="region" aria-label="Manage Aptean Admin Users">
      <Helmet>
        <meta name="ai:viewId" content="aptean-admin-users"></meta>
        <meta name="ai:viewDescription" content="Aptean Pay Merchant Portal - Aptean Admin Users"></meta>
        <title>Aptean Pay Merchant Portal - Aptean Admin Users</title>
      </Helmet>
      <Toolbar className={classes.toolbar} variant="regular">
        <Box className={classes.title} data-cy="manage-aptean-admin-users-header">
          <Typography variant="title">Manage Aptean Admin Users</Typography>
        </Box>
      </Toolbar>
      <Box className={classes.usersTable} data-cy="aptean-admin-users-table" role="region" aria-label="All Aptean Admin Users">
        <DataList
          gridOptions={gridOptions}
          isSkeletonHidden={true}
          cypressTag="aptean-admin-users-table-body"
          noDataText="No users to show."
          sortingMode="client"
        />
      </Box>
    </Paper>
  );
};

export default ApteanAdminUsers;
